import React from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { Form, Formik } from "formik";
import { toast } from "react-hot-toast";
import * as Yup from "yup";
import axios from "../../../api/axios";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import PMListbox from "../../utils/PMListbox";
import { projectParticipantsRole } from "./utils";

export const ProjectAccessDialog = ({ open, onClose, projectId, access, onSuccess }) => {
  const { t } = useTranslation();
  const isEditing = !!access;

  const handleSubmit = async (values, helpers) => {
    try {
      // Get current project data
      const projectResponse = await axios.get(`/projects/${projectId}`);
      const project = projectResponse.data;

      // Prepare participant data
      const participantData = {
        user_id: values.user_id,
        role: values.role,
        admin: values.admin || false,
        status: values.status || "ASSIGNED",
      };

      let updatedParticipants = [...(project.participants || [])];

      if (isEditing) {
        // Update existing participant
        participantData.id = access.id;
        updatedParticipants = updatedParticipants.map((p) =>
          p.id === access.id ? { ...p, ...participantData } : p
        );
      } else {
        // Add new participant
        updatedParticipants.push(participantData);
      }

      // Update project with new/updated participant
      const response = await axios.put(`/projects/${projectId}`, {
        project: {
          ...project,
          participants: updatedParticipants,
        },
      });

      if (response.status === 200) {
        // Fetch updated project to get the full participant data
        const updatedProjectResponse = await axios.get(`/projects/${projectId}`, {
          params: { fields: "participants" },
        });
        
        const updatedParticipants = updatedProjectResponse.data.participants || [];
        const savedAccess = isEditing
          ? updatedParticipants.find((p) => p.id === access.id)
          : updatedParticipants[updatedParticipants.length - 1];

        if (onSuccess) {
          onSuccess(savedAccess);
        }
        toast.success(isEditing ? t("project.participants.updated") : t("project.participants.created"));
        helpers.setSubmitting(false);
      }
    } catch (err) {
      console.error("Error saving project access:", err);
      toast.error(t("project.participants.save_error"));
      helpers.setSubmitting(false);
      helpers.setStatus({ success: false });
      helpers.setErrors({ submit: err.message });
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t("project.participants.edit") : t("project.participants.add")}
    >
      <div className="mt-4">
        <Formik
          initialValues={{
            user_id: access?.user?.id || null,
            role: access?.role || "EXECUTOR",
            admin: access?.admin || false,
            status: access?.status || "ASSIGNED",
            submit: null,
          }}
          validationSchema={Yup.object({
            user_id: Yup.string().required(t("project.participants.user_required")),
            role: Yup.string().required(t("project.participants.role_required")),
          })}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form>
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-6">
                  <EmployeeSelector
                    label={t("project.participants.user")}
                    name="user_id"
                    selected={access?.user}
                    {...formik}
                  />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <PMListbox
                    label={t("project.participants.role")}
                    name="role"
                    options={projectParticipantsRole}
                    selected={formik.values.role}
                    onChange={(e) => {
                      formik.setFieldValue("role", e.value);
                    }}
                    {...formik}
                  />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <PMField
                    name="admin"
                    type="checkbox"
                    label={t("project.participants.admin_access")}
                    {...formik}
                  />
                </div>
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                  onClick={onClose}
                >
                  {t("Cancel")}
                </button>

                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:bg-gray-400"
                >
                  {formik.isSubmitting
                    ? isEditing
                      ? t("project.participants.updating")
                      : t("project.participants.adding")
                    : isEditing
                    ? t("Update")
                    : t("project.participants.add")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </PMDialog>
  );
};

